import { instacarAppLanguageSchema } from "@instacover/helpers"
import { useParams } from "@tanstack/react-router"
import { useEffect } from "react"

import { getConfig } from "@/services/dexie"
import { logger } from "@/utils/monitoring/logging"

import { applyLanguage } from "./i18n"
import { readPersistedLanguage } from "./persistedLanguage"

/**
 * Applies the language cached for this session. Screens that render after the session loader failed and
 * before reaching applyLanguage would otherwise always be rendered in fallback language (cs).
 */
export function useCachedSessionLanguage() {
  // shouldThrow defaults to true even with strict: false, and this hook also renders in the root
  // notFoundComponent, where a missing match would blank the page instead of showing the error
  const params = useParams({ strict: false, shouldThrow: false })
  const sessionId = params?.sessionId

  useEffect(() => {
    if (sessionId === undefined) return

    getConfig(sessionId)
      .then((config) => {
        // no config -> fallback language will be used (cs)
        if (!config) return

        // Mirrors what the loader would have picked, organization bundle included
        const persisted = readPersistedLanguage(config.customerId)
        const isPersistedOffered = persisted !== undefined && config.languages.includes(persisted)

        // Dexie can hold a config written by an older app version, so never trust its shape
        const parsed = instacarAppLanguageSchema.safeParse(isPersistedOffered ? persisted : config.languages[0])

        if (!parsed.success) {
          return
        }

        return applyLanguage(parsed.data, config.organizationHumanReadableId)
      })
      .catch((error: unknown) => {
        logger.debug("Cached session language not applied", error)
      })
  }, [sessionId])
}
